import React, { useEffect, useState } from 'react';
import ApexCharts from 'react-apexcharts';
import { Box, Typography } from "@mui/material";
import { Assessment } from "../../../enums/Assessment";

const getNewestVersion = (rule) => rule.versions?.[rule.versions.length - 1];

const escapeHtml = (value) => String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');

const PrecisionByRule = ({ reviews, rules }) => {
    const [chartData, setChartData] = useState(null);

    useEffect(() => {
        if (!reviews || !rules) return;

        const reviewsByClause = reviews.reduce((acc, review) => {
            if (!acc[review.clause]) {
                acc[review.clause] = [];
            }
            acc[review.clause].push(review);
            return acc;
        }, {});

        const ruleStats = rules.reduce((acc, rule) => {
            const newestVersion = getNewestVersion(rule);

            acc[rule._id] = {
                title: rule.title,
                text: newestVersion?.text || '',
                total: 0,
                wrong: 0
            };
            return acc;
        }, {});

        Object.values(reviewsByClause).forEach(clauseReviews => {
            const llmReview = clauseReviews
                .filter(review => review.author?.kind === 'LLM')
                .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))[0];
            const humanReview = clauseReviews
                .filter(review => review.author?.kind !== 'LLM')
                .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))[0];

            if (!llmReview || llmReview.assessment !== Assessment.VOID || !llmReview.hurt_rules?.length) {
                return;
            }

            const wasCorrectedByLawyer = humanReview && humanReview.assessment !== llmReview.assessment;

            llmReview.hurt_rules.forEach(ruleId => {
                if (!ruleStats[ruleId]) return;

                ruleStats[ruleId].total += 1;
                if (wasCorrectedByLawyer) {
                    ruleStats[ruleId].wrong += 1;
                }
            });
        });

        const calculatedChartData = Object.values(ruleStats)
            .filter(rule => rule.total > 0)
            .map(rule => ({
                ...rule,
                correct: rule.total - rule.wrong,
                precision: (rule.total - rule.wrong) / rule.total,
                errorRate: rule.wrong / rule.total
            }))
            .sort((a, b) => b.precision - a.precision);

        setChartData(calculatedChartData);
    }, [reviews, rules]);

    if (!chartData) {
        return <></>;
    }

    return (
        <Box>
            <Typography variant="h5" component="h2" fontWeight="bold">Performance nach Prüfanleitung</Typography>
            <Typography color="text.secondary" sx={{ mt: 1, mb: 2 }}>
                Diese Analyse zeigt, wie oft eine Prüfanleitung von der KI bei einer als unwirksam bewerteten Klausel genannt wurde
                und wie häufig diese Bewertung durch die neueste Anwaltsbewertung korrigiert wurde. Niedrige Fehlanwendungsraten bedeuten,
                dass die Prüfanleitung in den vorhandenen Bewertungen zuverlässig angewandt wurde.
            </Typography>
            {chartData.length > 0 ?
                <ApexCharts
                    options={{
                        chart: {
                            type: 'bar',
                            stacked: true,
                            toolbar: { show: true }
                        },
                        plotOptions: {
                            bar: {
                                horizontal: true,
                                borderRadius: 3
                            }
                        },
                        xaxis: {
                            max: 100,
                            labels: {
                                formatter: (value) => `${Number(value).toFixed(0)}%`
                            },
                            title: {
                                text: 'Anteil der Nennungen'
                            }
                        },
                        yaxis: {
                            labels: {
                                maxWidth: 260
                            }
                        },
                        colors: ['#8DB255', '#D24D57'],
                        dataLabels: {
                            enabled: true,
                            formatter: (value) => `${Number(value).toFixed(1)}%`
                        },
                        tooltip: {
                            custom: ({ seriesIndex, dataPointIndex, w }) => {
                                const rule = chartData[dataPointIndex];

                                return `
                                    <div style="padding: 10px; max-width: 360px;">
                                        <strong>${escapeHtml(rule.title)}</strong>
                                        <div style="margin-top: 8px; white-space: normal;">${escapeHtml(rule.text)}</div>
                                    </div>
                                `;
                            }
                        },
                        legend: {
                            position: 'top'
                        }
                    }}
                    series={[
                        {
                            name: 'Nicht korrigiert',
                            data: chartData.map(rule => Number((rule.precision * 100).toFixed(2)))
                        },
                        {
                            name: 'Korrigiert',
                            data: chartData.map(rule => Number((rule.errorRate * 100).toFixed(2)))
                        }
                    ]}
                    type="bar"
                    height={Math.max(350, chartData.length * 36)}
                /> : <Typography>Nicht genug Daten für die Analyse nach Prüfanleitungen vorhanden.</Typography>
            }
        </Box>
    );
};

export default PrecisionByRule;
